/**
 * Builder-credit helpers. The balance itself is fetched by the existing `useBuilderBalance` hook
 * (which computes cents with grant-expiry logic); these helpers turn a cents balance into an
 * estimated number of message sends, and state the rate the sends are charged at.
 */
import { formatCentsToUsd } from '@/utils/currency';
import { formatNumber } from '@/utils/number';

/** The rate is quoted per this many sends, on every surface that shows it. */
const SENDS_PER_RATE_UNIT = 1000;

/**
 * Defensive fallback per-message cost in dollars. The canonical value comes from
 * `usage.builder_credit_cost_per_message`. Keeping the literal in one place prevents drift if the
 * canonical cost ever changes.
 */
export const FALLBACK_BUILDER_CREDIT_COST_PER_MESSAGE = 0.0004;

/**
 * Runtime defense: a malformed payload that serves 0 or a negative cost would otherwise divide to
 * Infinity and crash the number formatter downstream.
 */
function usableCostPerMessage(costPerMessage: number): number {
  return costPerMessage > 0 ? costPerMessage : FALLBACK_BUILDER_CREDIT_COST_PER_MESSAGE;
}

/**
 * Converts a dollar balance (in cents) to an estimated number of Builder sends, given the per-message
 * cost.
 */
export function estimatedSendsFromCents(cents: number, costPerMessage: number): number {
  const dollars = cents / 100;
  return Math.floor(dollars / usableCostPerMessage(costPerMessage));
}

/**
 * The drawdown rate as a customer reads it: `$0.40 per 1,000 sends`. The API serves a per-message
 * cost, and every surface that shows the rate shows it per thousand, so the conversion belongs here
 * rather than at each call site. Callers add their own punctuation.
 *
 * Rounds to whole cents per 1,000, which every served rate is. A rate between two cents would print
 * rounded beside a sends estimate computed from the exact figure, so the two would disagree.
 */
export function formatSendRatePerThousand(costPerMessage: number): string {
  const perThousandCents = Math.round(usableCostPerMessage(costPerMessage) * SENDS_PER_RATE_UNIT * 100);
  return `${formatCentsToUsd(perThousandCents)} per ${formatNumber(SENDS_PER_RATE_UNIT)} sends`;
}
